import { useContext, useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useInvalidateFilesQueries } from '@/api/hooks/useInvalidateFileQueries';
import type { UseModalProps } from '@trinity/chat-components/components/Modal/index.js';
import { Modal, Header, useModal } from '@trinity/chat-components/components/Modal/index.js';
import ButtonSet from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/index.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import type { GroupTagUpdateItem } from '@/types';
import { SelectedObjectsContext } from './context';
import { useSelectedObjects } from './hooks';
import styles from './style/ApplyTags.Modal.module.css';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { GroupTagSelect } from '@trinity/chat-components/components/AutoComplete/GroupTagSelect.js';

export const ApplyTagsModal = (props: UseModalProps) => {
  const [tags, setTags] = useState<GroupTagUpdateItem[]>([]);

  const invalidateFilesQueryies = useInvalidateFilesQueries();
  const { clearSelectedObjects } = useContext(SelectedObjectsContext);

  const onDone = useCallback(() => {
    invalidateFilesQueryies();
    clearSelectedObjects();
    props.onClose();
  }, [clearSelectedObjects, invalidateFilesQueryies, props]);

  //const [Screen, stepperActions] = useStepper([TagPickingScreen, AddToClientsScreen]);


  return (
    <Modal disableOverlayClick={true} {...props}>
      <TagPickingScreen
        onClose={props.onClose}
        onDone={onDone}
        tags={tags}
        setTags={setTags}
      />
    </Modal>
  );
};

type TagPickingScreenProps = {
  onClose: () => void;
  onDone: () => void;
  tags: GroupTagUpdateItem[];
  setTags: (tags: GroupTagUpdateItem[]) => void;
};

const TagPickingScreen = ({ onDone, onClose, tags, setTags }: TagPickingScreenProps) => {
  const selectedObjects = useSelectedObjects();
  const apiClient = useChatApiClient();

  const addTagsMutation = useMutation({
    mutationFn: () => apiClient.tags.massAddObjectTags({ objectIds: selectedObjects.map(o => o.object.id), tags: tags }),
    onError: () => Toast.error({ title: 'Error adding tags' }),
    onSuccess: () => {
      onDone();
    },
  });

  return (
    <>
      <Header>{`Apply Tags to ${selectedObjects.length} selected objects`}</Header>
      <GroupTagSelect onChange={setTags} />
      <ButtonSet className={styles.buttons}>
        <Button
          variant='brick'
          color='destructive'
          onClick={onClose}>
          Cancel
        </Button>
        <ButtonActivityIndicator
          disabled={addTagsMutation.isPending || !tags.length}
          implicitDisable={false}
          loading={addTagsMutation.isPending}
          onClick={() => addTagsMutation.mutateAsync()}>
          Add Tags
        </ButtonActivityIndicator>
      </ButtonSet>
    </>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApplyTagsModal = () => useModal(ApplyTagsModal);